import { LOGO_FULL_MAX_SIDE, LOGO_MARK_SIDE, LOGO_MAX_BYTES } from "@teamna/shared";
import {
  clearBox,
  findBox,
  inkStats,
  SMALL_LOGO_SIDE,
  squareSource,
  visibleBounds,
  WIDE_LOGO,
  type LogoFacts,
  type Pixels,
  type SquareFocus,
} from "./logo-pixels.ts";

/**
 * The browser side of Settings, Brand: read the owner's file into pixels, and turn pixels plus the
 * owner's choices into the two PNG files the API takes. The pixel work itself is in
 * `logo-pixels.ts`; this file only moves pixels in and out of a canvas.
 */

export const ACCEPTED_LOGO_TYPES = "image/png,image/jpeg,image/svg+xml,image/webp";
/** The largest file the owner can pick. The files we send are much smaller. */
export const LOGO_FILE_MAX_BYTES = 5 * 1024 * 1024;
/** Larger pictures are scaled down before any work, so the checks stay fast. */
const WORK_MAX_SIDE = 2048;
/** An SVG with no size of its own is drawn at this size. */
const SVG_DEFAULT_SIDE = 1024;

export type LogoSource = {
  fileName: string;
  pixels: Pixels;
  /** The size of the file as the owner picked it, for the "too small" check. */
  originalWidth: number;
  originalHeight: number;
};

export type LogoSettings = {
  removeBackground: boolean;
  plateOnDark: boolean;
  focus: SquareFocus;
};

export type LogoDraft = {
  /** The file has a box the owner can remove. */
  boxFound: boolean;
  /** The logo is wide enough to offer the left and middle squares. */
  wide: boolean;
  facts: LogoFacts;
  accent: string | null;
  full: { dataUrl: string; base64: string; width: number; height: number };
  mark: { dataUrl: string; base64: string };
};

export class LogoFileError extends Error {}

function canvasOf(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  return canvas;
}

function context(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new LogoFileError("This browser cannot read pictures. Try another browser.");
  return ctx;
}

function toCanvas(pixels: Pixels): HTMLCanvasElement {
  const canvas = canvasOf(pixels.width, pixels.height);
  context(canvas).putImageData(new ImageData(new Uint8ClampedArray(pixels.data), pixels.width, pixels.height), 0, 0);
  return canvas;
}

function pixelsOf(canvas: HTMLCanvasElement): Pixels {
  const image = context(canvas).getImageData(0, 0, canvas.width, canvas.height);
  return { data: image.data, width: canvas.width, height: canvas.height };
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new LogoFileError("We cannot open this picture. Save it as a PNG and try again."));
    image.src = url;
  });
}

/** Reads the owner's file into pixels. Throws a `LogoFileError` with words for the owner. */
export async function readLogoFile(file: File): Promise<LogoSource> {
  if (!ACCEPTED_LOGO_TYPES.split(",").includes(file.type)) {
    throw new LogoFileError("Choose a PNG, JPG, SVG or WebP file.");
  }
  if (file.size > LOGO_FILE_MAX_BYTES) {
    throw new LogoFileError(`This file is ${(file.size / 1048576).toFixed(1)} MB. Choose a file of 5 MB or less.`);
  }
  const url = URL.createObjectURL(file);
  try {
    const image = await loadImage(url);
    const svg = file.type === "image/svg+xml";
    let width = image.naturalWidth || (svg ? SVG_DEFAULT_SIDE : 0);
    let height = image.naturalHeight || (svg ? SVG_DEFAULT_SIDE : 0);
    if (width === 0 || height === 0) throw new LogoFileError("This picture is empty. Choose another file.");
    const originalWidth = width;
    const originalHeight = height;
    // An SVG has no real pixel size, so it is drawn large and never counts as small.
    if (svg) {
      const grow = SVG_DEFAULT_SIDE / Math.max(width, height);
      width *= grow;
      height *= grow;
    }
    const shrink = Math.min(1, WORK_MAX_SIDE / Math.max(width, height));
    const canvas = canvasOf(width * shrink, height * shrink);
    context(canvas).drawImage(image, 0, 0, canvas.width, canvas.height);
    let pixels: Pixels;
    try {
      pixels = pixelsOf(canvas);
    } catch {
      throw new LogoFileError("We cannot read this file. Save it as a PNG and try again.");
    }
    return {
      fileName: file.name,
      pixels,
      originalWidth: svg ? SVG_DEFAULT_SIDE : originalWidth,
      originalHeight: svg ? SVG_DEFAULT_SIDE : originalHeight,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function base64Of(dataUrl: string): string {
  return dataUrl.slice(dataUrl.indexOf(",") + 1);
}

function bytesOf(base64: string): number {
  const padding = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
  return (base64.length * 3) / 4 - padding;
}

/** The full logo as a PNG no larger than the API takes: 1024 px on the long side, less if needed. */
function fullPng(trimmed: HTMLCanvasElement): LogoDraft["full"] {
  for (const side of [LOGO_FULL_MAX_SIDE, 768, 512, 384]) {
    const scale = Math.min(1, side / Math.max(trimmed.width, trimmed.height));
    const canvas = canvasOf(trimmed.width * scale, trimmed.height * scale);
    const ctx = context(canvas);
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(trimmed, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/png");
    const base64 = base64Of(dataUrl);
    if (bytesOf(base64) <= LOGO_MAX_BYTES) return { dataUrl, base64, width: canvas.width, height: canvas.height };
  }
  throw new LogoFileError("This picture has too much detail. Use a simpler logo file.");
}

/** Turns the owner's file and choices into the two files and the facts for the checks. */
export function buildLogoDraft(source: LogoSource, settings: LogoSettings): LogoDraft {
  const box = findBox(source.pixels);
  const cleaned = box && settings.removeBackground ? clearBox(source.pixels, box) : source.pixels;
  const boxLeft = settings.removeBackground ? null : box;

  const bounds = visibleBounds(cleaned);
  if (!bounds) throw new LogoFileError("This picture is empty. Choose another file.");
  const whole = toCanvas(cleaned);
  const trimmed = canvasOf(bounds.width, bounds.height);
  context(trimmed).drawImage(whole, -bounds.x, -bounds.y);

  const aspect = trimmed.width / trimmed.height;
  const wide = aspect >= WIDE_LOGO;
  const focus = wide ? settings.focus : "whole";

  // The square: the chosen part of the logo, centred with 8% of air on each side.
  const part = squareSource(trimmed.width, trimmed.height, focus);
  const markCanvas = canvasOf(LOGO_MARK_SIDE, LOGO_MARK_SIDE);
  const markContext = context(markCanvas);
  markContext.imageSmoothingQuality = "high";
  const inner = LOGO_MARK_SIDE * 0.84;
  const scale = inner / Math.max(part.width, part.height);
  const drawWidth = part.width * scale;
  const drawHeight = part.height * scale;
  markContext.drawImage(
    trimmed,
    part.x,
    part.y,
    part.width,
    part.height,
    (LOGO_MARK_SIDE - drawWidth) / 2,
    (LOGO_MARK_SIDE - drawHeight) / 2,
    drawWidth,
    drawHeight,
  );
  const markDataUrl = markCanvas.toDataURL("image/png");
  const markBase64 = base64Of(markDataUrl);
  if (bytesOf(markBase64) > LOGO_MAX_BYTES) {
    throw new LogoFileError("This picture has too much detail. Use a simpler logo file.");
  }

  const markStats = inkStats(pixelsOf(markCanvas), boxLeft);
  const fullStats = inkStats(pixelsOf(trimmed), boxLeft);

  return {
    boxFound: box !== null,
    wide,
    accent: fullStats.accent,
    facts: {
      box: boxLeft,
      dark: markStats.darkShare > 0.55,
      light: markStats.lightShare > 0.6,
      small: Math.max(source.originalWidth, source.originalHeight) < SMALL_LOGO_SIDE,
      tinyText: aspect > 1.8 && focus === "whole",
      plateOnDark: settings.plateOnDark,
    },
    full: fullPng(trimmed),
    mark: { dataUrl: markDataUrl, base64: markBase64 },
  };
}
